import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  DEFAULT_CURRENCY_CODE,
  InjectionToken,
  NgModule,
  Optional,
  PLATFORM_ID,
} from '@angular/core';
import { BrowserModule, Title, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { isPlatformBrowser, registerLocaleData, ViewportScroller } from '@angular/common';
import { Event } from '@angular/router';
import { RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { filter, pairwise } from 'rxjs/operators';
import { BREAKPOINT, FlexLayoutModule, LayoutGapStyleBuilder } from '@angular/flex-layout';
import {
  FEATURE_TOGGLE_CONFIG_TOKEN,
  FeatureToggleConfig,
  FeatureToggleModule,
  QaLocatorsModule,
  StorageModule,
} from '@metromarkets/sdk';
import { MatDialogModule } from '@angular/material/dialog';
import {
  MM_BREAKPOINTS,
  MmAlertModule,
  MmButtonModule,
  MmDialogModule,
  MmFlexLayoutModule,
  MmIconModule,
} from '@metromarkets/components';
import { CookieService } from '~shared/services/cookie.service';
import { TOGGLE_URLS } from '~core/constants/feature-toggle-url';
import { RouterCustomSerializer, RouterEffects, routerReducer } from '~core/store';
import { L10nClientInterceptor } from '~core/interceptors/l10n.client.interceptor';
import { BuyerAccountModule } from '~core/modules/buyer-account/buyer-account.module';
import { ApplicationEventModule } from '~shared/modules/application-event/application-event.module';
import { NotFoundComponent } from '~shared/components/not-found/not-found.component';
import { UserInfoModule } from '~shared/modules/user-info/user-info.module';
import { ShoppingCartStoreModule } from '~shared/modules/shopping-cart-store';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GenericModule } from './modules/generic/generic.module';
import { API_CART_ROUTE, API_CHECK_INVENTORY_ROUTE } from '~app/modules/product/constants';
import { CartModule } from '~shared/modules/cart/cart.module';
import { ErrorPageModule } from '~shared/modules/error-page/error-page.module';
import { ServerErrorPageComponent } from '~shared/components/server-error-page/server-error.component';
import { IdentityModule } from '~core/modules/identity/identity.module';
import { CartAddFailedAlertComponent } from '~app/modules/product/components/alerts/cart-add-failed-alert/cart-add-failed-alert.component';
import { CartAddServerErrorAlertComponent } from '~app/modules/product/components/alerts/cart-add-server-error-alert/cart-add-server-error-alert.component';
import { NewsletterLandingModule } from './modules/newsletter-landing/newsletter-landing.module';

import { ShoppingCartGlobalModule } from '~shared/modules/shopping-cart-global/shopping-cart-global.module';
import { BasePageModule } from '~shared/modules/base-page/base-page.module';
import { ImpersonateComponent } from '~core/components/impersonate/impersonate.component';

import { ErrorHandlerModule } from '~shared/modules/error-handler';
import { APP_USER_LOGIN_ROUTE } from '~shared/constants';
import { LEGAL_PRIVACY_POLICY_LINK } from '~shared/constants/links.constants';
import { CookieConsentModule } from '~shared/modules/cookie-consent/cookie-consent.module';
import { METRO_COOKIE_NAME, METRO_COOKIE_VALUE } from '~shared/constants/buyer-cookie.constant';
import { ActiveFeaturesModule } from '~shared/modules/active-features/active-features.module';
import { MetaInfoService } from '~shared/services/meta-info.service';
import { SourcesInsertService } from '~shared/services/sources-insert.service';
import { GATrackingModule } from '~shared/modules/ga-tracking/ga-tracking.module';
import { IsVisibleModule } from '~shared/directives/is-visible/is-visible.module';
import { OAuthModule } from 'angular-oauth2-oidc';
import { MarketHeaderInterceptor } from '~core/interceptors/market-header.interceptor';
import { CSSValidationErrorInterceptor } from './modules/authorization/interceptors/css-validation-error.interceptor';
import { BlockedAccountComponent } from '~shared/components/blocked-account/blocked-account.component';
import { IdamLogoutListenerService } from '~app/modules/authorization/services/idam-logout-listener.service';
import { AuthorizationIdamStateService } from '~app/modules/authorization/services/authorization-idam-state.service';
import { Router, Scroll } from '@angular/router';
import { FeatureFlagQueryUtil } from '~shared/utils/feature-flag-query.util';
import { DEBUG, INFO } from 'browser-bunyan';
import { ServerLoggerConfig, ServerLoggerModule } from '~shared/modules/server-logger';
import { WeeeLandingPageModule } from '~shared/modules/weee-landing-page/weee-landing-page.module';

import { TransferHttpCacheModule } from '@nguniversal/common';
import { environment } from '~env/environment';
import { MESSAGE_FORMAT_CONFIG } from 'ngx-translate-messageformat-compiler';
import { L10nModule } from './core/modules/l10n/l10n.client.module';
import { CACHE_SERVICE } from './core/constants/tokens';
import { ClientCacheService } from './core/services/client-cache.service';
import { PriceTypeTogglerModule } from '~shared/modules/price-type-toggler/price-type-toggler.module';
import { HomepageNavigatorModule } from '~shared/modules/homepage-navigator/homepage-navigator.module';
import { CartNavigatorModule } from '~shared/modules/cart-navigator/cart-navigator.module';
import { CatalogInterceptor } from '~app/modules/catalog/interceptors/catalog.interceptor';
import { StoryblokModule } from './modules/storyblok/storyblok.module';
import { StoryblokService } from './modules/storyblok/services';
import { DynamicModule } from 'ng-dynamic-component';
import { DynamicComponentModule } from '~shared/directives/dinamicComponent/dynamicComponent.module';

// locale imports and registering
import { Locale } from '~shared/constants/locale.enum';
import localeDE from '@angular/common/locales/de';
import localeES from '@angular/common/locales/es';
import localeIT from '@angular/common/locales/it';
import localePT from '@angular/common/locales/pt-PT';
import localeNL from '@angular/common/locales/nl';
import localeFR from '@angular/common/locales/fr';
import { OptimizelyService } from '~shared/services/optimizely.service';
import { CustomLayoutGapStyleBuilder } from '~shared/services/custom-layout-gap-style-builder';
import { ProductsLoadingService } from '~app/modules/catalog/services/products-loading.service';
import { SubsidiaryAlertModule } from '~shared/components/subsidiary-alert/subsidiary-alert.module';
import { isMMCompanion } from '~server/utils/auth.utils';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { SkipToMenuComponent } from '~shared/components/skip-to-menu/skip-to-menu.component';

registerLocaleData(localeDE);
registerLocaleData(localeES);
registerLocaleData(localeIT);
// needed to have 'pt' sent for currency formatting
registerLocaleData(localePT, 'pt');
registerLocaleData(localeNL);
registerLocaleData(localeFR);

export function iconFactory() {
  return { baseUrl: `${environment.cdnBaseUrl()}/images/icons` };
}

export function cartFactory() {
  return {
    cartApiUrl: API_CART_ROUTE(),
    checkInventoryApiUrl: API_CHECK_INVENTORY_ROUTE(),
  };
}

export function errorConfigFactory() {
  return {
    loginRoute: APP_USER_LOGIN_ROUTE,
    forbiddenRoute: 'access-forbiden',
  };
}

export function cookieConsentFactory() {
  return {
    cookieValue: METRO_COOKIE_VALUE,
    cookieName: METRO_COOKIE_NAME,
    cookiePolicyUrl: LEGAL_PRIVACY_POLICY_LINK,
  };
}

export function serverLoggerConfigFactory(): ServerLoggerConfig {
  return {
    logEndpoint: `${environment.baseHref()}log`,
    logLevel: environment.appEnv() === 'prod' ? INFO : DEBUG,
    httpMethod: 'PUT',
  };
}

export const IS_COMPANION = new InjectionToken<boolean>('isCompanion');
export function isCompanionFactory(platformId, req): boolean {
  if (isPlatformBrowser(platformId)) {
    return isMMCompanion();
  }
  return req?.get?.('user-agent')?.includes?.('Companion') ?? false;
}

@NgModule({
  declarations: [
    AppComponent,
    NotFoundComponent,
    ServerErrorPageComponent,
    CartAddFailedAlertComponent,
    CartAddServerErrorAlertComponent,
    ImpersonateComponent,
    BlockedAccountComponent,
    SkipToMenuComponent
  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'serverApp' }),
    TransferHttpCacheModule,
    OAuthModule.forRoot(),
    BrowserAnimationsModule,
    StorageModule.forRoot(),
    ApplicationEventModule,
    GATrackingModule,
    HttpClientModule,
    ErrorHandlerModule.forRoot(errorConfigFactory), // Module should be imported *before* BuyerAccountModule
    BuyerAccountModule,
    IdentityModule,
    AppRoutingModule,
    StoreRouterConnectingModule.forRoot(),
    StoreModule.forRoot(routerReducer),
    EffectsModule.forRoot([RouterEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      //logOnly: environment.production
    }),
    QaLocatorsModule.forRoot({
      shouldSetupLocators: true,
    }),
    FlexLayoutModule.withConfig({ disableDefaultBps: true }),
    MmFlexLayoutModule,
    CartModule.forRoot(cartFactory),
    ShoppingCartStoreModule,
    ShoppingCartGlobalModule,
    L10nModule,
    MmAlertModule,
    MmIconModule.forRoot(iconFactory),
    GenericModule,
    ErrorPageModule,
    MmButtonModule,
    UserInfoModule,
    BasePageModule,
    CookieConsentModule.forRoot(cookieConsentFactory),
    ServerLoggerModule.forRoot(serverLoggerConfigFactory),
    FeatureToggleModule.forRoot({
      provide: FEATURE_TOGGLE_CONFIG_TOKEN,
      useFactory: (): FeatureToggleConfig => ({
        toggleUrl: TOGGLE_URLS(),
      }),
    }),
    ActiveFeaturesModule.forRoot(),
    IsVisibleModule,
    HammerModule,
    NewsletterLandingModule,
    MatDialogModule,
    WeeeLandingPageModule,
    MmDialogModule,
    PriceTypeTogglerModule,
    HomepageNavigatorModule,
    StoryblokModule,
    DynamicModule,
    DynamicComponentModule,
    CartNavigatorModule,
    SubsidiaryAlertModule,
  ],
  providers: [
    Title,
    CookieService,
    SourcesInsertService,
    MetaInfoService,
    StoryblokService,
    { provide: BREAKPOINT, useValue: MM_BREAKPOINTS, multi: true },
    { provide: RouterStateSerializer, useClass: RouterCustomSerializer },
    { provide: HTTP_INTERCEPTORS, useClass: L10nClientInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: MarketHeaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CSSValidationErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CatalogInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: (logoutListener: IdamLogoutListenerService, authIdamService: AuthorizationIdamStateService) => () => {
        authIdamService.initOAuth();
        logoutListener.initLogoutListener();
      },
      deps: [IdamLogoutListenerService, AuthorizationIdamStateService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (router: Router) => () => {
        FeatureFlagQueryUtil.initializeLocalStorageFeaturesFromQuery(router);
      },
      deps: [Router],
      multi: true,
    },
    {
      provide: MESSAGE_FORMAT_CONFIG,
      useValue: {
        locales: [Locale.de, Locale.en, Locale.es, Locale.it, Locale.pt, Locale.nl, Locale.fr],
      },
    },
    {
      provide: CACHE_SERVICE,
      useFactory: () => new ClientCacheService(),
      deps: [],
    },
    OptimizelyService,
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'EUR',
    },
    { provide: LayoutGapStyleBuilder, useClass: CustomLayoutGapStyleBuilder },
    ProductsLoadingService,
    {
      provide: IS_COMPANION,
      useFactory: isCompanionFactory,
      deps: [PLATFORM_ID, [new Optional(), REQUEST]],
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private router: Router, private viewportScroller: ViewportScroller) {
    // Disable automatic scroll restoration to avoid race conditions
    this.viewportScroller.setHistoryScrollRestoration('manual');
    this.handleScrollOnNavigation();
  }

  /**
   * When route is changed, Angular interprets a simple query params change as "forward navigation" too.
   * Using the pairwise function allows us to have both the previous and current router events, which we can
   * use to effectively compare the two navigation events and see if they actually change route, or only
   * the route parameters (i.e. selections stored in query params).
   *
   * Related to: https://github.com/angular/angular/issues/26744
   */
  private handleScrollOnNavigation(): void {
    this.router.events
      .pipe(
        filter((e: Event): e is Scroll => e instanceof Scroll),
        pairwise()
      )
      .subscribe((e: Scroll[]) => {
        const previous = e[0];
        const current = e[1];
        if (current.position) {
          // Backward navigation
          this.viewportScroller.scrollToPosition(current.position);
        } else if (current.anchor) {
          // Anchor navigation
          this.viewportScroller.scrollToAnchor(current.anchor);
        } else {
          // Check if routes match, or if it is only a query param change
          if (
            this.getBaseRoute(previous.routerEvent.urlAfterRedirects) !==
            this.getBaseRoute(current.routerEvent.urlAfterRedirects)
          ) {
            // Routes don't match, this is actual forward navigation
            // Default behavior: scroll to top
            this.viewportScroller.scrollToPosition([0, 0]);
          }
        }
      });
  }

  private getBaseRoute(url: string): string {
    // return url without query params
    return url.split('?')[0];
  }
}
